import React from 'react'
import styled, { keyframes, css } from 'styled-components'
import {
  Box,
  Button,
  Flex,
  HelpIcon,
  Link,
  LinkExternal,
  MetamaskIcon,
  Skeleton,
  Text,
  TimerIcon,
  useTooltip,
  useModal,
} from '@pancakeswap/uikit'
import { BASE_BSC_SCAN_URL } from 'config'
import { getBscScanLink } from 'utils'
import { useBlock } from 'state/block/hooks'
import { useCakeVault } from 'state/pools/hooks'
import BigNumber from 'bignumber.js'
import { Domain } from 'state/types'
import { useTranslation } from 'contexts/Localization'
import Balance from 'components/Balance'
import { CompoundingPoolTag, ManualPoolTag } from 'components/Tags'
import { getAddress, getCakeVaultAddress } from 'utils/addressHelpers'
import { registerToken } from 'utils/wallet'
import { getBalanceNumber, getFullDisplayBalance, formatNumber } from 'utils/formatBalance'
import { getPoolBlockInfo } from 'views/Pools/helpers'
import useGetNFTPrice from 'hooks/useGetNFTPrice'
import Harvest from './Harvest'
import Stake from './Stake'
import Apr from '../Apr'
import SettingModal from '../../PoolCard/Modals/SettingModal'
import RenewModal from '../../PoolCard/Modals/RenewModal'
import TransferModal from '../../PoolCard/Modals/TransferModal'

const expandAnimation = keyframes`
  from {
    max-height: 0px;
  }
  to {
    max-height: 700px;
  }
`

const collapseAnimation = keyframes`
  from {
    max-height: 700px;
  }
  to {
    max-height: 0px;
  }
`

const StyledActionPanel = styled.div<{ expanded: boolean }>`
  animation: ${({ expanded }) =>
    expanded
      ? css`
          ${expandAnimation} 300ms linear forwards
        `
      : css`
          ${collapseAnimation} 300ms linear forwards
        `};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.dropdownDeep};
  box-shadow: rgba(149, 157, 165, 0.2) 0px 8px 24px;
  display: flex;
  // flex-direction: column-reverse;
  flex-direction: column;
  justify-content: center;
  padding: 12px;

  ${({ theme }) => theme.mediaQueries.lg} {
    flex-direction: row;
    padding: 16px 32px;
  }
`

const ActionContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: end;

  ${({ theme }) => theme.mediaQueries.sm} {
    flex-direction: row;
    align-items: center;
    flex-grow: 1;
    flex-basis: 0;
  }
`

type MediaBreakpoints = {
  isXs: boolean
  isSm: boolean
  isMd: boolean
  isLg: boolean
  isXl: boolean
}

interface ActionPanelProps {
  account: string
  pool: Domain
  userDataLoaded: boolean
  expanded: boolean
  breakpoints: MediaBreakpoints
}

const InfoSection = styled(Box)`
  flex-grow: 0;
  flex-shrink: 0;
  flex-basis: auto;
  padding: 8px 8px;
  ${({ theme }) => theme.mediaQueries.lg} {
    padding: 0;
    flex-basis: 230px;
  }
`

const ActionPanel: React.FC<ActionPanelProps> = ({ account, pool, userDataLoaded, expanded, breakpoints }) => {
  const { tokenId, tokenURI, nftName, nameXTExt, expiryTime } = pool
  const { t } = useTranslation()
  const pricePerYear = useGetNFTPrice()
  const [onPresentSetting] = useModal(
    <SettingModal
      tokenId={tokenId}
      nftName={nftName}
      nameXTExt={nameXTExt}
      tokenURI={tokenURI}
      pricePerYear={pricePerYear ? new BigNumber(pricePerYear.toString()) : new BigNumber(0)}
    />,
  )
  const [onPresentRenew] = useModal(
    <RenewModal
      tokenId={tokenId}
      nftName={nftName}
      nameXTExt={nameXTExt}
      tokenURI={tokenURI}
      expiryTime={expiryTime}
      pricePerYear={pricePerYear ? new BigNumber(pricePerYear.toString()) : new BigNumber(0)}
    />,
  )

  const [onPresentTransfer] = useModal(
    <TransferModal
      tokenId={tokenId}
      nftName={nftName}
      nameXTExt={nameXTExt}
      tokenURI={tokenURI}
      pricePerYear={pricePerYear ? new BigNumber(pricePerYear.toString()) : new BigNumber(0)}
    />,
  )

  const onSetting = () => {
    onPresentSetting()
  }

  const onRenew = () => {
    onPresentRenew()
  }

  const onTransfer = () => {
    onPresentTransfer()
  }

  /*
  const poolContractAddress = contractAddress ? getAddress(contractAddress) : ""
  const cakeVaultContractAddress = getCakeVaultAddress()
  const { currentBlock } = useBlock()
  const { isXs, isSm, isMd } = breakpoints
  const showSubtitle = (isXs || isSm) && sousId === 0

  const { shouldShowBlockCountdown, blocksUntilStart, blocksRemaining, hasPoolStarted, blocksToDisplay } =
    getPoolBlockInfo(pool, currentBlock)

  const isMetaMaskInScope = !!window.ethereum?.isMetaMask
  const tokenAddress = earningToken && earningToken.address ? getAddress(earningToken.address) : ''

  const {
    totalCakeInVault,
    fees: { performanceFee },
  } = useCakeVault()

  const performanceFeeAsDecimal = performanceFee && performanceFee / 100
  const isManualCakePool = sousId === 0

  const getTotalStakedBalance = () => {
    if (isAutoVault) {
      return getBalanceNumber(totalCakeInVault, stakingToken.decimals)
    }
    if (isManualCakePool) {
      const manualCakeTotalMinusAutoVault = new BigNumber(totalStaked).minus(totalCakeInVault)
      // return getBalanceNumber(manualCakeTotalMinusAutoVault, stakingToken.decimals)
      return getBalanceNumber(totalStaked, stakingToken.decimals)
    }
    return getBalanceNumber(totalStaked, stakingToken.decimals)
  }

  const getTotalStakedValue = () => {
    if (isAutoVault) {
      return getBalanceNumber(totalCakeInVault.multipliedBy(stakingTokenPrice), stakingToken.decimals)
    }
    if (isManualCakePool) {
      const manualCakeTotalMinusAutoVault = new BigNumber(totalStaked).minus(totalCakeInVault)
      // return getBalanceNumber(manualCakeTotalMinusAutoVault, stakingToken.decimals)
      return getBalanceNumber(totalStaked.multipliedBy(stakingTokenPrice), stakingToken.decimals)
    }
    return getBalanceNumber(totalStaked.multipliedBy(stakingTokenPrice), stakingToken.decimals)
  }

  const {
    targetRef: totalStakedTargetRef,
    tooltip: totalStakedTooltip,
    tooltipVisible: totalStakedTooltipVisible,
  } = useTooltip(t('Total amount of %symbol% staked in this pool', { symbol: stakingToken ? stakingToken.symbol : "" }), {
    placement: 'bottom',
  })

  const manualTooltipText = t('You must harvest and compound your earnings from this pool manually.')
  const autoTooltipText = t(
    'Any funds you stake in this pool will be automagically harvested and restaked (compounded) for you.',
  )

  const {
    targetRef: tagTargetRef,
    tooltip: tagTooltip,
    tooltipVisible: tagTooltipVisible,
  } = useTooltip(isAutoVault ? autoTooltipText : manualTooltipText, {
    placement: 'bottom-start',
  })

  const maxStakeRow = stakingLimit && stakingLimit.gt(0) ? (
    <Flex mb="8px" justifyContent="space-between">
      <Text>{t('Max. stake per user')}:</Text>
      <Text>{`${getFullDisplayBalance(stakingLimit, stakingToken.decimals, 0)} ${stakingToken.symbol}`}</Text>
    </Flex>
  ) : null

  const blocksRow =
    blocksRemaining || blocksUntilStart ? (
      <Flex mb="8px" justifyContent="space-between">
        <Text>{hasPoolStarted ? t('Ends in') : t('Starts in')}:</Text>
        <Flex>
          <Link external href={getBscScanLink(hasPoolStarted ? endBlock : startBlock, 'countdown')}>
            <Balance fontSize="16px" value={blocksToDisplay} decimals={0} color="primary" />
            <Text ml="4px" color="primary" textTransform="lowercase">
              {t('Blocks')}
            </Text>
            <TimerIcon ml="4px" color="primary" />
          </Link>
        </Flex>
      </Flex>
    ) : (
      <Skeleton width="56px" height="16px" />
    )

  const aprRow = (
    <Flex justifyContent="space-between" alignItems="center" mb="8px">
      <Text>{isAutoVault ? t('APY') : t('APR')}:</Text>
      <Apr pool={pool} showIcon performanceFee={isAutoVault ? performanceFeeAsDecimal : 0} />
    </Flex>
  )

  const totalStakedRow = (
    <Flex justifyContent="space-between" alignItems="center" mb="8px">
      <Text maxWidth={['50px', '100%']}>{t('Total staked')}:</Text>
      <Flex alignItems="center">
        {totalStaked && totalStaked.gte(0) ? (
          <>
            <div>
              <Balance fontSize="16px" value={getTotalStakedBalance()} decimals={0} unit={` ${stakingToken.symbol}`} />
              <Text style={{ marginBottom: "0px", marginTop: "0px" }}>{`$${formatNumber(getTotalStakedValue())}`}</Text>
            </div>
            <span ref={totalStakedTargetRef}>
              <HelpIcon color="textSubtle" width="20px" ml="4px" />
            </span>
          </>
        ) : (
          <Skeleton width="56px" height="16px" />
        )}
        {totalStakedTooltipVisible && totalStakedTooltip}
      </Flex>
    </Flex>
  )
          */
  return (
    <StyledActionPanel expanded={expanded}>
      <InfoSection>
        <Flex mb="8px" justifyContent={['flex-end', 'flex-end', 'flex-start']}>
          {tokenURI && (
            <LinkExternal href={tokenURI} bold={false} color="#ff6335">
              <Text mt="10px">{tokenURI}</Text>
            </LinkExternal>
          )}
        </Flex>
      </InfoSection>
      <ActionContainer>
        <Button mr="20px" mt="10px" onClick={onSetting} style={{ height: '30px' }}>
          {t('Setting')}
        </Button>
        <Button mr="20px" mt="10px" onClick={onRenew} style={{ height: '30px' }}>
          {Date.now() < getBalanceNumber(expiryTime, 0) * 1000 ? t('Extend') : t('Renew')}
        </Button>
        <Button mr="20px" mt="10px" onClick={onTransfer} style={{ height: '30px' }}>
          {t('Transfer')}
        </Button>
      </ActionContainer>
      {/*
      <InfoSection>
        {maxStakeRow}
        {(isXs || isSm) && aprRow}
        {(isXs || isSm || isMd) && totalStakedRow}
        {shouldShowBlockCountdown && blocksRow}
        <Flex mb="8px" justifyContent={['flex-end', 'flex-end', 'flex-start']}>
          <LinkExternal href={`https://pancakeswap.info/token/${earningToken ? getAddress(earningToken.address) : ""}`} bold={false} color="#ff6335">
            {t('See Token Info')}
          </LinkExternal>
        </Flex>
        <Flex mb="8px" justifyContent={['flex-end', 'flex-end', 'flex-start']}>
          <LinkExternal href={earningToken ? earningToken.projectLink : ""} bold={false} color="#ff6335" style={{ color: '#ff6335' }}>
            {t('View Project Site')}
          </LinkExternal>
        </Flex>
        {poolContractAddress && (
          <Flex mb="8px" justifyContent={['flex-end', 'flex-end', 'flex-start']}>
            <LinkExternal
              href={`${BASE_BSC_SCAN_URL}/address/${isAutoVault ? cakeVaultContractAddress : poolContractAddress}`}
              bold={false}
              color="#ff6335"
            >
              {t('View Contract')}
            </LinkExternal>
          </Flex>
        )}
        {account && isMetaMaskInScope && tokenAddress && (
          <Flex mb="8px" justifyContent={['flex-end', 'flex-end', 'flex-start']}>
            <Button
              variant="text"
              p="0"
              height="auto"
              onClick={() => registerToken(tokenAddress, earningToken.symbol, earningToken.decimals)}
            >
              <Text color="primary">{t('Add to Metamask')}</Text>
              <MetamaskIcon ml="4px" />
            </Button>
          </Flex>
        )}
        {isAutoVault ? <CompoundingPoolTag /> : <ManualPoolTag />}
        {tagTooltipVisible && tagTooltip}
        <span ref={tagTargetRef}>
          <HelpIcon ml="4px" width="20px" height="20px" color="#212121" />
        </span>
      </InfoSection>
      <ActionContainer>
        {showSubtitle && (
          <Text mt="4px" mb="16px" color="textSubtle">
            {isAutoVault ? t('Automatic restaking') : `${t('Earn')} XTT-b20 ${t('Stake').toLocaleLowerCase()} XTT-b20`}
          </Text>
        )}
        <Harvest {...pool} userDataLoaded={userDataLoaded} />
        <Stake pool={pool} userDataLoaded={userDataLoaded} />
      </ActionContainer>
        */}
    </StyledActionPanel>
  )
}

export default ActionPanel
